import {
  GenerateImagePreviewDocument,
  type GenerateImagePreviewQuery,
  type GenerateImagePreviewQueryVariables,
  GetMediaDocument,
  ImageQuality,
  ImageSize,
  LinkMediaDocument,
  type LinkMediaMutation,
  type LinkMediaMutationVariables,
  MediaType,
  SaveGeneratedImageDocument,
  type SaveGeneratedImageMutation,
  type SaveGeneratedImageMutationVariables,
} from "@/generated/graphql";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MediaLinksPanel, TagsSection } from "../forms";
import { ModalShell } from "./ModalShell";

interface TextToImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId?: string | null;
  mediaType: MediaType;
}

export function TextToImageModal({
  isOpen,
  onClose,
  currentFolderId,
  mediaType,
}: TextToImageModalProps) {
  // Input form state
  const [prompt, setPrompt] = useState("");
  const [filename, setFilename] = useState("");
  const [size, setSize] = useState<ImageSize>(ImageSize.Square);
  const [quality, setQuality] = useState<ImageQuality>(ImageQuality.Standard);

  // Preview state
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [revisedPrompt, setRevisedPrompt] = useState<string | null>(null);

  // Metadata state
  const [tags, setTags] = useState<string[]>([]);
  const [selectedLinks, setSelectedLinks] = useState<Partial<Record<MediaType, string[]>>>({});

  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Preview query
  const [generatePreview] = useLazyQuery<
    GenerateImagePreviewQuery,
    GenerateImagePreviewQueryVariables
  >(GenerateImagePreviewDocument, {
    fetchPolicy: "network-only",
  });

  // Save mutation
  const [saveGeneratedImage, { loading: isSaving }] = useMutation<
    SaveGeneratedImageMutation,
    SaveGeneratedImageMutationVariables
  >(SaveGeneratedImageDocument, {
    refetchQueries: [
      {
        query: GetMediaDocument,
        variables: { folderId: currentFolderId || undefined, type: mediaType },
      },
    ],
  });

  // Link media mutation
  const [linkMedia] = useMutation<LinkMediaMutation, LinkMediaMutationVariables>(LinkMediaDocument);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setPrompt("");
        setFilename("");
        setSize(ImageSize.Square);
        setQuality(ImageQuality.Standard);
        setImageUrl(null);
        setImageBlob(null);
        setImageBase64(null);
        setRevisedPrompt(null);
        setTags([]);
        setSelectedLinks({});
        setGenerationError(null);
      }, 300);
    }
  }, [isOpen]);

  // Clean up blob URL when component unmounts or image changes
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  const handleClose = () => {
    if (imageUrl) {
      URL.revokeObjectURL(imageUrl);
    }
    onClose();
  };

  // Handle link changes
  const handleLinksChange = (type: MediaType, ids: string[]) => {
    setSelectedLinks((prev) => ({
      ...prev,
      [type]: ids,
    }));
  };

  // Generate image preview
  const handleGenerateImage = async (e: FormEvent) => {
    e.preventDefault();

    if (!prompt.trim()) {
      toast.error("Please enter a prompt to generate an image");
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const { data, error } = await generatePreview({
        variables: {
          prompt,
          size,
          quality,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.generateImagePreview.success) {
        throw new Error(data?.generateImagePreview.error || "Image generation failed");
      }

      if (!data.generateImagePreview.imageBase64) {
        throw new Error("No image data returned");
      }

      // Convert base64 to blob
      const binaryString = atob(data.generateImagePreview.imageBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "image/png" });
      const url = URL.createObjectURL(blob);

      // Clean up old URL if exists
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }

      setImageBlob(blob);
      setImageUrl(url);
      setImageBase64(data.generateImagePreview.imageBase64);
      setRevisedPrompt(data.generateImagePreview.revisedPrompt || null);
      toast.success("Image preview generated!");
    } catch (error) {
      setGenerationError(error instanceof Error ? error.message : "Failed to generate image");
      toast.error(error instanceof Error ? error.message : "Failed to generate image");
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate with same settings
  const handleRegenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt first");
      return;
    }

    await handleGenerateImage({ preventDefault: () => {} } as FormEvent);
  };

  // Save to library
  const handleSave = async () => {
    if (!imageBase64 || !imageBlob) {
      toast.error("Please generate image preview first");
      return;
    }

    if (!filename.trim()) {
      toast.error("Please enter a filename");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    try {
      const result = await saveGeneratedImage({
        variables: {
          input: {
            imageData: imageBase64,
            filename,
            folderId: currentFolderId || undefined,
            tags,
          },
        },
      });

      const savedImageId = result.data?.textToImage.id;

      // Create media links if any
      if (savedImageId) {
        for (const [, linkIds] of Object.entries(selectedLinks)) {
          for (const linkId of linkIds || []) {
            try {
              await linkMedia({
                variables: {
                  input: {
                    fromId: savedImageId,
                    toId: linkId,
                  },
                },
              });
            } catch (linkError) {
              // Continue even if linking fails (might be duplicate)
              console.warn("Link creation failed:", linkError);
            }
          }
        }
      }

      toast.success(`Image generated successfully: ${result.data?.textToImage.filename}`);
      handleClose();
    } catch (error) {
      toast.error(
        `Failed to save image: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={isSaving || isGenerating ? () => {} : handleClose}
      title="Generate Image from Text"
      singleColumn={true}
      leftPanel={
        <div className="space-y-4">
          {/* Filename Input */}
          <section>
            <label htmlFor="filename" className="mb-2 block text-sm font-medium text-gray-700">
              Filename <span className="text-red-500">*</span>
            </label>
            <input
              id="filename"
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="my-image-file"
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">
              The file will be saved as {filename || "filename"}.png
            </p>
          </section>

          {/* Image Preview (shows when generated) */}
          {imageUrl && (
            <section>
              <label className="mb-2 block text-sm font-medium text-gray-700">Image preview</label>
              <div className="rounded-md border border-gray-200 p-3">
                <img
                  src={imageUrl}
                  alt="Generated preview"
                  className="w-full rounded-md"
                  style={{ maxHeight: "400px", objectFit: "contain" }}
                />
              </div>
              {revisedPrompt && (
                <div className="mt-2 rounded-md bg-blue-50 p-3">
                  <p className="text-xs font-medium text-blue-900">DALL-E's interpretation:</p>
                  <p className="mt-1 text-xs text-blue-700">{revisedPrompt}</p>
                </div>
              )}
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={isGenerating || isSaving}
                className="mt-3 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isGenerating ? "Generating..." : "Regenerate (New Variation)"}
              </button>
            </section>
          )}

          {/* Prompt Input */}
          <section>
            <label htmlFor="prompt" className="mb-2 block text-sm font-medium text-gray-700">
              Prompt <span className="text-red-500">*</span>
            </label>
            <textarea
              id="prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              rows={3}
              maxLength={4000}
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">{prompt.length}/4000 characters</p>
          </section>

          {/* Options Grid - Size, Quality */}
          <div className="grid grid-cols-2 gap-4">
            {/* Size Selector */}
            <section>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Size <span className="text-red-500">*</span>
              </label>
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value={ImageSize.Square}
                    checked={size === ImageSize.Square}
                    onChange={() => setSize(ImageSize.Square)}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-blue-600"
                  />
                  <span className="text-xs">Square</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value={ImageSize.Landscape}
                    checked={size === ImageSize.Landscape}
                    onChange={() => setSize(ImageSize.Landscape)}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-blue-600"
                  />
                  <span className="text-xs">Landscape</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="size"
                    value={ImageSize.Portrait}
                    checked={size === ImageSize.Portrait}
                    onChange={() => setSize(ImageSize.Portrait)}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-blue-600"
                  />
                  <span className="text-xs">Portrait</span>
                </label>
              </div>
            </section>

            {/* Quality Selector */}
            <section>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Quality <span className="text-red-500">*</span>
              </label>
              <div className="space-y-1">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="quality"
                    value={ImageQuality.Standard}
                    checked={quality === ImageQuality.Standard}
                    onChange={() => setQuality(ImageQuality.Standard)}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-blue-600"
                  />
                  <span className="text-xs">Standard</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="quality"
                    value={ImageQuality.Hd}
                    checked={quality === ImageQuality.Hd}
                    onChange={() => setQuality(ImageQuality.Hd)}
                    disabled={isGenerating || isSaving}
                    className="h-3.5 w-3.5 text-blue-600"
                  />
                  <span className="text-xs">HD</span>
                </label>
              </div>
            </section>
          </div>

          {/* Generate Button */}
          <button
            type="button"
            onClick={handleGenerateImage}
            disabled={isGenerating || isSaving || !prompt.trim()}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isGenerating ? "Generating..." : "Generate Image Preview"}
          </button>

          {generationError && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-800">{generationError}</p>
            </div>
          )}

          {/* Tags Section */}
          <TagsSection value={tags} onChange={setTags} required />

          {/* Media Links */}
          <MediaLinksPanel
            sourceMediaType={MediaType.Image}
            sourceMediaId={null}
            selectedLinks={selectedLinks}
            onLinksChange={handleLinksChange}
          />

          {/* Save Button */}
          <div className="flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSaving || isGenerating}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                isSaving || isGenerating || !imageBlob || !filename.trim() || tags.length === 0
              }
              className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save to Library"}
            </button>
          </div>
        </div>
      }
    />
  );
}
